import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"
import { ReservationStatus } from "./reservation"
import { User } from "./user"

const fleetReservationSchema = z.object({
    userId: z.string().min(1),
    startTime: z.custom<Timestamp>(),
    endTime: z.custom<Timestamp>(),
    canceled: z.boolean().default(false),
    dateCanceled: z.custom<Timestamp>().nullish(),
    fleetCarId: z.string().min(1),
    timestampCreated: z.custom<Timestamp>().nullish(),
    note: z.string().nullish(),
    odometerInitial: z.number().nullish(),
    odometerFinal: z.number().nullish()
})

interface IFleetReservation {
    id: string
    userId: string
    startTime: Date
    endTime: Date
    canceled: boolean
    dateCanceled?: Date
    fleetCarId: string
    timestampCreated?: Date
    note?: string
    odometerInitial?: number
    odometerFinal?: number
}

export interface FleetReservation extends IFleetReservation {}
export class FleetReservation implements IFleetReservation {
    // Helper properties
    user?: User

    constructor(params: IFleetReservation) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): FleetReservation | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create FleetReservation ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = fleetReservationSchema.parse(raw)
            return new FleetReservation({
                id: doc.id,
                userId: parsed.userId,
                startTime: parsed.startTime.toDate(),
                endTime: parsed.endTime.toDate(),
                canceled: parsed.canceled,
                dateCanceled: parsed.dateCanceled?.toDate() ?? undefined,
                fleetCarId: parsed.fleetCarId,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                note: parsed.note ?? undefined,
                odometerInitial: parsed.odometerInitial ?? undefined,
                odometerFinal: parsed.odometerFinal ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create FleetReservation ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return fleetReservationSchema.parse({
            userId: this.userId,
            startTime: this.startTime,
            endTime: this.endTime,
            canceled: this.canceled,
            dateCanceled: this.dateCanceled,
            fleetCarId: this.fleetCarId,
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            note: this.note,
            odometerInitial: this.odometerInitial,
            odometerFinal: this.odometerFinal
        })
    }

    status(): ReservationStatus {
        return this.canceled ? ReservationStatus.Canceled : ReservationStatus.Confirmed
    }

    toString(): string {
        return `${this.userId} → ${this.fleetCarId} @ ${this.startTime.toISOString()}`
    }
}
